<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <AppPageHeader design="new" title="Communication" compact />
    <AppTabs design="new" :tabs="tabs" refined weight="medium" />
    <Templates v-if="queue === 'templates'" />
    <main v-else class="ui-page">
    <AppFilterToolbar>
      <template #primary><AppInput id="communication-search" :value="filters.search" design="new" type="search" placeholder="Guest or group" aria-label="Search" maxlength="255" @input="scheduleSearch" /></template>
      <AppSelect :value="filters.assignee_ids" design="new" input-id="communication-assignees" aria-label="Responsible" :options="assignees" :reduce="option => option.id" label="full_name" multiple :disabled="assigneesLoading" placeholder="Responsible" @input="changeFilter('assignee_ids', $event)" />
      <AppSelect :value="filters.contact_type" design="new" input-id="communication-type" aria-label="Kind" :options="contactTypes" :reduce="option => option.value" label="label" placeholder="Kind" @input="changeFilter('contact_type', $event)" />
      <template #selection><div class="communication__toggles"><AppCheckbox design="new" label="My tasks" :value="mineSelected" @change="toggleMine" /><AppCheckbox v-if="queue === 'needs_attention'" design="new" label="Overdue only" :value="Boolean(filters.overdue)" @change="changeFilter('overdue', $event)" /></div></template>
      <template #actions>
        <AppIconButton v-if="canEdit" label="Plan contacts" tooltip-placement="left" @click="showPlan = true"><i class="pi pi-calendar-plus" aria-hidden="true" /></AppIconButton>
        <AppIconButton label="About Communication" tooltip-placement="left" @click="showInfo = true"><i class="pi pi-info-circle" aria-hidden="true" /></AppIconButton>
      </template>
      <template v-if="hasFilters" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
    </AppFilterToolbar>

    <div v-if="actionError" class="communication__error" role="alert">
      <span>{{ actionError }}</span><AppButton design="new" theme="dark" @click.native="actionError = ''">Dismiss</AppButton>
    </div>
    <AppErrorState v-if="error" :message="error" icon-only @retry="load" />
    <AppContentSkeleton v-else-if="loading" label="Loading contacts" :columns="columns" min-width="1200px" />

    <template v-else-if="groupedItems.length">
      <AppCard v-for="section in groupedItems" :key="section.value" design="new" variant="table" header-density="compact" :title="section.label" :count="section.items.length">
        <AppDataTable design="new" density="dense" :columns="columns" :rows="section.items" min-width="1200px">
        <template #cell-due_at="{ row }">
          <span class="communication__cell">
            <AppTableText :text="dueLabel(row)" />
            <span v-if="row.is_overdue" v-tooltip="tip('Overdue')" class="communication__warning ui-tooltip-target" tabindex="0" aria-label="Overdue"><i class="pi pi-clock" aria-hidden="true" /></span>
          </span>
        </template>
        <template #cell-group="{ row }">
          <div class="communication__group-cell">
            <span v-if="row.group.language" class="communication__language ui-tooltip-target" tabindex="0" :aria-label="`Group language: ${row.group.language}`"
              v-tooltip="tip(`Group language: ${row.group.language}`)">
              <span v-if="languageFlagCode(row.group.language)" :class="`fflag ff-sm fflag-${languageFlagCode(row.group.language)}`" aria-hidden="true" />
              <i v-else class="pi pi-globe" aria-hidden="true" />
            </span>
            <router-link :to="{ name: 'safari-groups-detail', params: { id: row.group.id } }" class="communication__group"><AppTableText :text="row.group.name" /></router-link>
          </div>
        </template>
        <template #cell-client="{ row }">
          <router-link v-if="row.client" :to="{ name: 'clients-detail', params: { id: row.client.id } }"
            class="communication__group"><AppTableText :text="row.client.full_name" /></router-link>
          <template v-else>—</template>
        </template>
        <template #cell-communication="{ row }">
          <div class="communication__contact" aria-label="Communication channels">
            <AppIconButton :label="row.client && row.client.phone ? `${copyLabel(row, 'phone')}: ${row.client.phone}` : 'Phone not provided'" :disabled="!row.client || !row.client.phone" @click="copyContact(row, 'phone')"><i class="pi" :class="copiedKey === `${row.id}:phone` ? 'pi-check' : 'pi-phone'" aria-hidden="true" /></AppIconButton>
            <AppIconButton :label="row.client && row.client.phone && row.client.phone_whatsapp ? `Open WhatsApp: ${row.client.phone}` : 'WhatsApp not available'" :disabled="!row.client || !row.client.phone || !row.client.phone_whatsapp || !whatsappLink(row.client.phone)" @click="openWhatsApp(row.client.phone)"><i class="pi pi-whatsapp" aria-hidden="true" /></AppIconButton>
            <AppIconButton :label="row.client && row.client.email ? `${copyLabel(row, 'email')}: ${row.client.email}` : 'Email not provided'" :disabled="!row.client || !row.client.email" @click="copyContact(row, 'email')"><i class="pi" :class="copiedKey === `${row.id}:email` ? 'pi-check' : 'pi-envelope'" aria-hidden="true" /></AppIconButton>
            <AppIconButton label="Choose message template" @click="copyTask = row"><i class="pi pi-file" aria-hidden="true" /></AppIconButton>
          </div>
        </template>
        <template #cell-dates="{ row }"><AppTableText :text="safariDates(row.group)" /></template>
        <template #cell-status="{ row }">
          <span v-if="row.blocked_reason" v-tooltip="tip(row.blocked_reason)" class="ui-tooltip-target" tabindex="0">
            <AppStatusBadge design="new" compact tone="red" label="Missing info" />
          </span>
          <span v-else class="communication__muted">—</span>
        </template>
        <template #cell-assignee="{ row }">
          <AppTableUser v-if="row.assignee" :name="row.assignee.full_name" :photo="(row.assignee.crop && row.assignee.crop.crop_url) || ''" />
          <AppTableText v-else text="Unassigned" />
        </template>
        <template #cell-done="{ row }">
          <AppTableUser v-if="row.done_by" :name="`${row.done_by.full_name} · ${dateTime(row.done_at)}`" :photo="(row.done_by.crop && row.done_by.crop.crop_url) || ''" />
          <span v-else class="communication__muted">—</span>
        </template>
        <template #cell-response="{ row }">
          <span v-if="row.response && row.response.note && row.response.note !== responseLabel(row.response.result)" v-tooltip="tip(row.response.note)" class="ui-tooltip-target" tabindex="0">
            <AppStatusBadge design="new" compact :tone="responseTone(row.response.result)" :label="responseLabel(row.response.result)" />
          </span>
          <AppStatusBadge v-else-if="row.response" design="new" compact :tone="responseTone(row.response.result)" :label="responseLabel(row.response.result)" />
          <span v-else class="communication__muted">—</span>
        </template>
        <template #cell-actions="{ row }">
          <div class="communication__actions">
            <AppIconButton v-for="action in row.available_actions" :key="action" :label="actionLabel(action)" @click="act(row, action)">
              <i class="pi" :class="actionIcon(action)" aria-hidden="true" />
            </AppIconButton>
          </div>
        </template>
        </AppDataTable>
      </AppCard>
    </template>
    <AppNoResults v-else design="new" title="No contacts" text="" />
    <AppPagination v-if="!error && !loading" design="new" placement="embedded" :page="filters.page" :count="count"
      :visible-count="items.length" :page-size="40" :loading="loading" @change="changePage" />
    </main>
    <ActionModal v-if="modal" :task="modal.task" :mode="modal.mode" @close="modal = null" @saved="onSaved" />
    <TemplateCopyModal v-if="copyTask" :task="copyTask" @close="copyTask = null" />
    <InfoModal v-if="showInfo" @close="showInfo = false" />
    <PlanModal v-if="showPlan && canEdit" @close="showPlan = false" @completed="onPlanCompleted" />
  </AppPageContainer>
</template>

<script>
import { getSafariCommunication, getSafariCommunicationAssignees, getSafariCommunicationCounters, postSafariCommunicationAction } from '@/http'
import AppTabs from '@/components/AppTabs'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import { groundOpsAccess, groundOpsKey } from '@/utils/groundOpsAccess'
import ActionModal from './ActionModal'
import InfoModal from './InfoModal'
import PlanModal from './PlanModal'
import TemplateCopyModal from './TemplateCopyModal'
import Templates from './Templates'
import { ACTION_ICONS, ACTION_LABELS, QUEUES, RESPONSES, dateTime, dueLabel, errorMessage, responseOption, safariDates } from './model'

const LANGUAGE_FLAG_COUNTRIES = {
  english: 'GB',
  russian: 'RU',
  french: 'FR',
  german: 'DE',
  italian: 'IT',
  spanish: 'ES',
  portuguese: 'PT',
  swahili: 'TZ',
  chinese: 'CN',
  arabic: 'SA'
}

const defaultFilters = () => ({ search: '', contact_type: null, assignee_ids: [], overdue: false, page: 1 })
const COLUMNS = [
  { key: 'due_at', label: 'Due', width: '9%' },
  { key: 'group', label: 'Group', width: '10%' },
  { key: 'client', label: 'Guest', width: '10%' },
  { key: 'communication', label: 'Communication', width: '13%' },
  { key: 'dates', label: 'Safari dates', width: '9%' },
  { key: 'status', label: 'Attention', width: '9%' },
  { key: 'assignee', label: 'Responsible', width: '10%' },
  { key: 'done', label: 'Done by', width: '9%' },
  { key: 'response', label: 'Response', width: '8%' },
  { key: 'actions', label: '', width: '13%' }
]

export default {
  name: 'GroundOpsCommunicationList',
  components: { ActionModal, AppTabs, AppContentSkeleton, AppErrorState, AppIconButton, AppTableText, AppTableUser, InfoModal, PlanModal, TemplateCopyModal, Templates },
  props: {
    queue: { type: String, required: true, validator: value => value === 'templates' || Object.keys(QUEUES).includes(value) }
  },
  data() {
    return {
      items: [],
      count: 0,
      loading: false,
      error: '',
      actionError: '',
      modal: null,
      copyTask: null,
      showInfo: false,
      showPlan: false,
      copiedKey: '',
      copyTimer: null,
      searchTimer: null,
      requestNumber: 0,
      filters: defaultFilters(),
      columns: COLUMNS,
      assignees: [],
      assigneesLoading: false,
      counters: { upcoming: 0, needs_attention: 0, completed: 0 },
      contactTypes: [
        { value: 'welcome', label: 'Welcome' },
        { value: 'starts_tomorrow', label: 'Safari starts tomorrow' },
        { value: 'evening_follow_up', label: 'Evening follow-up' }
      ]
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](groundOpsKey('communication', 'edit')) },
    mineSelected() {
      return this.filters.assignee_ids.length === 1 && Number(this.filters.assignee_ids[0]) === Number(this.$store.state.userData.id)
    },
    queueInfo() { return QUEUES[this.queue] },
    groupedItems() {
      return this.contactTypes.map(type => ({
        ...type,
        items: this.items.filter(item => item.contact_type === type.value)
      })).filter(section => section.items.length)
    },
    tabs() {
      return [
        { name: 'ground-ops-communication-upcoming', label: 'Upcoming', count: this.counters.upcoming ?? 0 },
        { name: 'ground-ops-communication-needs-attention', label: 'Needs attention', count: this.counters.needs_attention ?? 0, countTheme: 'danger' },
        { name: 'ground-ops-communication-completed', label: 'Archive', count: this.counters.completed ?? 0 },
        { name: 'ground-ops-communication-templates', label: 'Templates', access: groundOpsAccess('communication', 'manage_templates') }
      ]
    },
    hasFilters() { return Boolean(this.filters.search || this.filters.contact_type || this.filters.assignee_ids.length || this.filters.overdue) },
    emptyText() { return this.hasFilters ? 'Try another search or clear the filters.' : this.queueInfo.description }
  },
  watch: {
    queue(value, previous) {
      clearTimeout(this.searchTimer)
      this.filters = defaultFilters()
      this.items = []
      this.requestNumber += 1
      if (this.queue === 'templates') {
        this.actionError = ''
        this.modal = null
        this.copyTask = null
        this.showInfo = false
        this.showPlan = false
      } else {
        this.load()
        if ((previous === 'templates' || !this.assignees.length) && !this.assigneesLoading) this.loadAssignees()
      }
    }
  },
  created() {
    this.loadCounters()
    if (this.queue !== 'templates') { this.load(false); this.loadAssignees() }
  },
  beforeDestroy() { clearTimeout(this.searchTimer); clearTimeout(this.copyTimer); this.requestNumber += 1 },
  methods: {
    dateTime,
    dueLabel,
    safariDates,
    languageFlagCode(language) { return LANGUAGE_FLAG_COUNTRIES[String(language || '').trim().toLowerCase()] || '' },
    actionLabel(action) { return ACTION_LABELS[action] || action },
    actionIcon(action) { return ACTION_ICONS[action] || 'pi-circle' },
    whatsappLink(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      return digits ? `https://wa.me/${digits}` : ''
    },
    openWhatsApp(phone) { window.open(this.whatsappLink(phone), '_blank', 'noopener,noreferrer') },
    copyLabel(row, field) { return `${this.copiedKey === `${row.id}:${field}` ? 'Copied' : 'Copy'} ${field}` },
    async copyContact(row, field) {
      try {
        await navigator.clipboard.writeText(row.client[field])
        this.copiedKey = `${row.id}:${field}`
        clearTimeout(this.copyTimer)
        this.copyTimer = setTimeout(() => { this.copiedKey = '' }, 2000)
      } catch (error) {
        this.actionError = `Could not copy ${field}.`
      }
    },
    tip(content) { return { content: String(content || ''), html: false, trigger: 'hover focus', placement: 'top', offset: 8, classes: 'tooltip--new' } },
    responseLabel(value) { return (responseOption(value) || {}).label || value },
    responseTone(value) { return (responseOption(value) || RESPONSES[1]).tone },
    async loadAssignees() {
      this.assigneesLoading = true
      try {
        this.assignees = (await getSafariCommunicationAssignees()).data
      } catch (error) {
        this.actionError = 'Could not load responsible employees.'
      } finally {
        this.assigneesLoading = false
      }
    },
    async loadCounters() {
      try {
        this.counters = (await getSafariCommunicationCounters()).data
      } catch (error) {
        // Counters are a convenience; the list itself reports its own errors.
      }
    },
    async load(refreshCounters = true) {
      const requestNumber = ++this.requestNumber
      this.loading = true
      this.error = ''
      try {
        const response = await getSafariCommunication({
          queue: this.queue,
          page: this.filters.page,
          search: this.filters.search || undefined,
          contact_type: this.filters.contact_type || undefined,
          is_overdue: this.filters.overdue ? true : undefined,
          assignee_ids: this.filters.assignee_ids.length ? this.filters.assignee_ids.join(',') : undefined
        })
        if (requestNumber !== this.requestNumber) return
        this.items = response.data.results
        this.count = response.data.count
        if (refreshCounters) this.loadCounters()
      } catch (error) {
        if (requestNumber !== this.requestNumber) return
        this.error = 'Could not load contacts.'
      } finally {
        if (requestNumber === this.requestNumber) this.loading = false
      }
    },
    scheduleSearch(value) {
      this.filters.search = (value || '').trimStart()
      this.filters.page = 1
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.load, 350)
    },
    changeFilter(key, value) { this.filters[key] = key === 'assignee_ids' ? (value || []) : (value || null); this.filters.page = 1; this.load() },
    toggleMine() { this.changeFilter('assignee_ids', this.mineSelected ? [] : [this.$store.state.userData.id]) },
    changePage(page) { this.filters.page = page; this.load() },
    clearFilters() {
      clearTimeout(this.searchTimer)
      this.filters = defaultFilters()
      this.load()
    },
    async act(task, action) {
      if (!this.canEdit) return
      if (action === 'claim') {
        try {
          await postSafariCommunicationAction(task.id, 'claim')
          this.onSaved()
        } catch (error) {
          this.actionError = errorMessage(error, 'Could not take this contact.')
        }
        return
      }
      this.modal = { task, mode: action }
    },
    onSaved() {
      this.actionError = ''
      this.load()
    },
    onPlanCompleted() {
      this.actionError = ''
      this.filters.page = 1
      this.load()
    }
  }
}
</script>

<style scoped lang="scss">
.communication__error { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-16; padding: $ui-space-12; border-radius: $ui-control-radius; background: $ui-red-soft; color: $ui-red; }
.communication__group { display: block; flex: 1 1 auto; min-width: 0; overflow: hidden; color: $ui-blue; text-decoration: none; }
.communication__group-cell, .communication__cell, .communication__contact, .communication__actions { display: flex; min-width: 0; align-items: center; gap: $ui-space-4; }
.communication__toggles { display: flex; align-items: center; flex-wrap: wrap; gap: $ui-space-12; }
.communication__language { display: inline-flex; width: 23px; height: 18px; flex: 0 0 auto; align-items: center; justify-content: center; color: $ui-muted; }
.communication__language .fflag { width: 23px; height: 14px; }
.communication__warning { color: $ui-red; }
.communication__muted { color: $ui-muted; }
</style>
